import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { throwFromResponse } from '../lib/apiErrorMessage';

/**
 * BE 정본 enum (`com.everline.user.domain.user.MemberType`) = `PERSONAL | BUSINESS`.
 * (`INDIVIDUAL` 은 과거 seed 오타 — V12 migration 이 PERSONAL 로 교정함. JPA enum 매핑은
 * PERSONAL/BUSINESS 만 유효.) admin 회원목록(`GET /api/admin/v1/users`)/상세는 PERSONAL 을
 * 내려주므로, 라벨 가드(userLabels)가 이 값을 인식해야 "—" 폴백을 피한다(F-1).
 * 안전상 legacy `INDIVIDUAL` 도 허용한다(라벨맵도 두 키 모두 보유).
 */
export type MemberType = 'PERSONAL' | 'BUSINESS' | 'INDIVIDUAL';
/**
 * BE 정본 enum (`com.everline.user.domain.user.UserStatus`).
 * R6 O — 기업회원 승인 게이트 추가로 PENDING_APPROVAL/REJECTED 신설(BE V56 라운드).
 * 개인(PERSONAL) 회원은 이 두 상태를 갖지 않으며, 일반 회원관리 화면의 상태 필터/전이
 * (UserListPage·UserTable.nextStatusOf)는 기존 4상태만 노출한다.
 */
export type MemberStatus =
  | 'ACTIVE'
  | 'DORMANT'
  | 'SUSPENDED'
  | 'WITHDRAWN'
  | 'PENDING_APPROVAL'
  | 'REJECTED';

export interface AdminMemberSummary {
  userId: string;
  /** QA-396 — 목록 첫 칸 로그인 ID. BE 목록 응답에 추가 요청(전달 문서). 없으면 '—'. */
  loginId?: string | null;
  /**
   * QA-430 — 회원 등급 id(목록 응답). BE 목록에 아직 없어 사업자회원 목록 등급 칸은 BE 반영 전 '—'(전달 문서).
   * 이름은 등급 마스터(useUserGradesQuery)에서 찾는다.
   */
  gradeId?: string | null;
  /** QA-430 (user v1.1.36) — 등급명. null 이고 gradeId 만 있으면 등급 마스터로 보완한다. */
  gradeName?: string | null;
  /**
   * 소속 국가몰(BCP-47 `ko-KR/en-US/ja-JP/zh-CN`). 회원이 국가몰 단위로 분리되어 같은
   * 아이디·이메일이 몰마다 별개 계정으로 존재할 수 있다(userMall.ts 참고).
   * 구버전 BE 응답에는 없을 수 있어 optional — 라벨은 userMallLabel 이 '—' 로 폴백한다.
   */
  mallCode?: string | null;
  name: string;
  email: string;
  mobileNumber: string;
  memberType: MemberType;
  status: MemberStatus;
  locked: boolean;
  nationality: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  /**
   * R6 O — 사업자회원 관리 목록에서 상호/사업자등록번호를 노출하기 위한 선택 필드.
   * 일반 회원목록 응답에는 비어 있을 수 있으므로 optional. (BUSINESS 회원만 채워짐)
   */
  businessName?: string | null;
  businessRegistrationNo?: string | null;
  /**
   * B2B(2026-06-20) D4 — 사업자등록증 파일 URL(승인 모달 미리보기용).
   * 목록 응답에 포함되지 않을 수 있어 optional. 누락 시 모달이 상세를 조회해 보강한다.
   */
  businessDocumentUrl?: string | null;
}

export interface AdminMemberDetail {
  userId: string;
  /** 소속 국가몰 — AdminMemberSummary.mallCode 와 동일(상세 상단 표시용). */
  mallCode?: string | null;
  name: string;
  email: string;
  mobileNumber: string;
  loginId: string;
  /**
   * QA-399 — 가입(로그인) 유형. user-service users.signup_provider: null = 일반 가입, KAKAO/NAVER/APPLE = SNS 가입.
   * BE AdminMemberDetail 에 추가 요청(전달 문서). 필드가 아예 없으면(undefined) 화면은 "—"(일반으로 단정하지 않는다).
   */
  signupProvider?: string | null;
  /** user #45 — 연동된 SNS(중복 없음, 알파벳순). 없으면 []. SNS 가입 회원은 자기 가입 SNS 도 들어 있을 수 있다. */
  linkedProviders?: ReadonlyArray<string> | null;
  birthDate: string | null;
  memberType: MemberType;
  status: MemberStatus;
  /**
   * B2B(2026-06-20) 계약 §3 — 부여된 등급 ID(라벨 매핑용, 화면 직접 노출 금지).
   * BUSINESS 회원만 의미가 있고 PERSONAL 은 null(등급 미부여, D1 등급 UI 숨김).
   */
  gradeId?: string | null;
  locked: boolean;
  lockedUntil: string | null;
  marketingEmailOptIn: boolean;
  marketingSmsOptIn: boolean;
  businessName: string | null;
  businessRegistrationNo: string | null;
  /** B2B(2026-06-20) D4 — 사업자등록증 파일 URL(상세/승인 모달 미리보기용). */
  businessDocumentUrl?: string | null;
  nationality: string | null;
  emailVerifiedAt: string | null;
  phoneVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * be-user V60(feat/admin-member-detail-extra) — 최근 휴면 해제 일시(ISO-8601). 해제 이력이 없으면 null.
   * 아래 주소 필드와 함께 구버전 응답에는 없어 optional.
   */
  dormancyReleasedAt?: string | null;
  /**
   * 회원정보 주소(고도몰 회원정보 주소 이관분) — 배송지 목록과 별개다. 상세주소는 unmask=false 면 "****",
   * 원문은 unmask=true(권한자)에서만 온다(이름·연락처와 같은 방식). 휴면회원은 셋 다 null.
   */
  memberPostalCode?: string | null;
  memberAddress?: string | null;
  memberAddressDetail?: string | null;
  /**
   * 사업자 정보(be-user feat/admin-member-detail-extra). 개인회원·미입력은 null, 구버전 응답에는 없다.
   * 대표자명은 unmask=false 면 이름과 같은 규칙으로, 사업장 상세주소는 "****" 로 가려 온다.
   */
  businessCeoName?: string | null;
  /** 업태. */
  businessCategory?: string | null;
  /** 종목. */
  businessItem?: string | null;
  businessPostalCode?: string | null;
  businessAddress?: string | null;
  businessAddressDetail?: string | null;
}

interface AdminMemberListEnvelope {
  success: boolean;
  data?: {
    items?: AdminMemberSummary[];
    content?: AdminMemberSummary[];
    meta?: { total: number; page: number; size: number };
    totalElements?: number;
    number?: number;
    size?: number;
  } | null;
}

interface AdminMemberDetailEnvelope {
  success: boolean;
  data?: AdminMemberDetail | null;
}

export interface AdminUsersQueryParams {
  keyword?: string | undefined;
  status?: MemberStatus | null | undefined;
  /** 소속 국가몰 필터(`?mallCode=ko-KR`). null/미지정=전체 몰. */
  mallCode?: string | null | undefined;
  /** B2B(2026-06-20) — 회원 유형 검색 조건(개인/기업). null=전체. legacy INDIVIDUAL 미사용. */
  memberType?: 'PERSONAL' | 'BUSINESS' | null | undefined;
  nationality?: string | null | undefined;
  grade?: string | null | undefined;
  joinedFrom?: string | null | undefined;
  joinedTo?: string | null | undefined;
  lastLoginFrom?: string | null | undefined;
  lastLoginTo?: string | null | undefined;
  emailOptIn?: boolean | null | undefined;
  smsOptIn?: boolean | null | undefined;
  marketingOptIn?: boolean | null | undefined;
  mileageMin?: number | null | undefined;
  mileageMax?: number | null | undefined;
  page?: number | undefined;
  size?: number | undefined;
  unmask?: boolean | undefined;
}

export interface AdminUsersPage {
  items: ReadonlyArray<AdminMemberSummary>;
  meta: { total: number; page: number; size: number };
}

const buildQuery = (params: AdminUsersQueryParams): string => {
  const q = new URLSearchParams();
  if (params.keyword) q.set('keyword', params.keyword);
  if (params.status) q.set('status', params.status);
  if (params.mallCode) q.set('mallCode', params.mallCode);
  if (params.memberType) q.set('memberType', params.memberType);
  if (params.nationality) q.set('nationality', params.nationality);
  if (params.grade) q.set('grade', params.grade);
  if (params.joinedFrom) q.set('joinedFrom', params.joinedFrom);
  if (params.joinedTo) q.set('joinedTo', params.joinedTo);
  if (params.lastLoginFrom) q.set('lastLoginFrom', params.lastLoginFrom);
  if (params.lastLoginTo) q.set('lastLoginTo', params.lastLoginTo);
  if (params.emailOptIn != null) q.set('emailOptIn', String(params.emailOptIn));
  if (params.smsOptIn != null) q.set('smsOptIn', String(params.smsOptIn));
  if (params.marketingOptIn != null) q.set('marketingOptIn', String(params.marketingOptIn));
  if (params.mileageMin != null) q.set('mileageMin', String(params.mileageMin));
  if (params.mileageMax != null) q.set('mileageMax', String(params.mileageMax));
  if (params.page != null) q.set('page', String(Math.max(0, params.page - 1)));
  if (params.size != null) q.set('size', String(params.size));
  if (params.unmask) q.set('unmask', 'true');
  return q.toString();
};

export const adminUsersQueryKey = (params: AdminUsersQueryParams) =>
  ['admin', 'users', params] as const;

export const adminUserDetailQueryKey = (userId: string, unmask = false) =>
  ['admin', 'users', userId, { unmask }] as const;

export const useAdminUsersQuery = (params: AdminUsersQueryParams) =>
  useQuery<AdminUsersPage>({
    queryKey: adminUsersQueryKey(params),
    queryFn: async () => {
      const qs = buildQuery(params);
      const res = await adminFetch(`/api/admin/v1/users${qs ? '?' + qs : ''}`);
      if (!res.ok) await throwFromResponse(res, '회원 목록을 불러오지 못했습니다.');
      const env = (await res.json()) as AdminMemberListEnvelope;
      const d = env.data;
      const items = d?.items ?? d?.content ?? [];
      const meta = d?.meta ?? {
        total: d?.totalElements ?? items.length,
        page: d?.number ?? 0,
        size: d?.size ?? 20,
      };
      return { items, meta };
    },
  });

export const useAdminUserDetailQuery = (userId: string, unmask = false) =>
  useQuery<AdminMemberDetail>({
    queryKey: adminUserDetailQueryKey(userId, unmask),
    queryFn: async () => {
      const qs = unmask ? '?unmask=true' : '';
      const res = await adminFetch(`/api/admin/v1/users/${userId}${qs}`);
      if (!res.ok) await throwFromResponse(res, '회원 정보를 불러오지 못했습니다.');
      const env = (await res.json()) as AdminMemberDetailEnvelope;
      if (!env.data) throw new Error('회원 정보 응답이 비어 있습니다.');
      return env.data;
    },
    enabled: Boolean(userId),
  });

export interface UserStatusPayload {
  status: MemberStatus;
  reason?: string;
}

export const useAdminUserStatusMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, payload }: { userId: string; payload: UserStatusPayload }) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/status`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwFromResponse(res, '회원 상태 변경에 실패했습니다.');
    },
    onSuccess: (_data, { userId }) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: adminUserDetailQueryKey(userId) });
    },
  });
};

export const useAdminUserUnlockMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await adminFetch(`/api/admin/v1/users/${userId}/unlock`, {
        method: 'POST',
      });
      if (!res.ok) await throwFromResponse(res, '계정 잠금 해제에 실패했습니다.');
    },
    onSuccess: (_data, userId) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: adminUserDetailQueryKey(userId) });
    },
  });
};
